<template>
  <div
    ref="listWrapperRef"
    class="border border-gray-250 rounded-lg overflow-hidden divide-y divide-y-gray-250 bg-white"
    :class="{'shadow-card-soft': model.length}"
  >
    <div
      v-if="isDragging !== null && previousPosition !== isDragging"
      aria-live="assertive"
      class="sr-only"
      role="status"
    >
      {{ t('announcements.position', {position: isDragging + 1}) }}
    </div>
    <div
      v-for="(item, index) in model"
      :key="keyFn(item, index)"
      class="group/row w-full flex justify-start items-stretch divide-x divide-gray-200 bg-gradient-to-t from-background to-background/50"
      :data-list-type="itemType ? itemType(item) : 'fart'"
      :data-json="JSON.stringify(item)"
    >
      <button
        v-if="sortable"
        class="text-gray-600 hover:text-blue-500 flex-none flex items-center justify-center cursor-grab group/handle grid grid-cols-1 grid-rows-1 py-2.5 pl-3 pr-2.5 hover:opacity-100 transition-all ease-out duration-150"
        :class="{
          'bg-blue-50 text-blue-600': isDragging === index,
          'opacity-30 bg-white hover:bg-gray-200/20': isDragging !== index,
        }"
        type="button"
        v-bind="dnd.props(index)"
        v-on="dnd.listeners(index)"
      >
        <span
          class="transition-all ease-out duration-150 group-hover/handle:opacity-0 row-start-1 col-start-1 grid grid-cols-1 grid-rows-1"
          :class="{'opacity-0': isDragging === index}"
        >
          <s-icon
            name="dots-vertical"
            size="20"
            class="-translate-x-[0.1875rem] row-start-1 col-start-1"
          />
          <s-icon
            name="dots-vertical"
            size="20"
            class="translate-x-[0.1875rem] row-start-1 col-start-1"
          />
        </span>
        <s-icon
          name="unfold-more-horizontal"
          size="20"
          class="row-start-1 col-start-1 transition-all ease-out duration-150 opacity-0 group-hover/handle:opacity-100"
          :class="{
            'text-blue-600 opacity-100': isDragging === index,
            'text-blue-500': isDragging !== index,
          }"
        />
      </button>
      <div class="flex-1 min-w-0 w-full">
        <slot name="item" v-bind="{item, index}">
          <div class="px-5 py-3">
            {{ displayValue?.(item) ?? item }}
          </div>
        </slot>
      </div>
      <button
        v-if="removable"
        @click.prevent="tryToRemoveItem(index)"
        class="text-red-300 hover:text-red-500 flex-none flex items-center justify-center py-2.5 pl-2.5 pr-3 opacity-70 hover:opacity-100 transition-all ease-out duration-150 bg-white hover:bg-gray-200/20 disabled:text-gray-300 disabled:bg-transparent"
      >
        <s-icon name="trash-can-outline" size="20" />
      </button>
    </div>
    <s-placeholder class="s-placeholder !border-0 !rounded-none">
      <slot name="placeholder" :placeholder="placeholder">
        {{ placeholder || 'No items' }}
      </slot>
    </s-placeholder>
  </div>

  <s-modal
    v-model:open="showRemoveConfirmationModal"
    :title="removeLabel || 'Delete Item'"
    :message="`Are you sure you want to delete ${displayValue?.(model[indexToRemove] as any) ?? 'this item'}?`"
    :confirm="{
      label: 'Delete',
      icon: 'trash-can',
      color: 'red',
    }"
    @confirm="
      dnd.remove(indexToRemove);
      showRemoveConfirmationModal = false;
    "
    cancellable
  />
</template>

<script setup lang="ts">
import {computed, nextTick, onMounted, onUnmounted, ref} from 'vue';
import SModal from './SModal.vue';
import SPlaceholder from './SPlaceholder.vue';
import SIcon from './SIcon.vue';
import {useI18n} from 'vue-i18n';
import {useDragAndDrop} from '../composables/useDragAndDrop.js';

const props = defineProps<{
  placeholder?: string;
  itemKey?: string | ((item: any) => string | number | undefined);
  itemType?: (item: any) => string;
  displayValue?: (item: any) => string;
  removeLabel?: string;
  confirmRemove?: boolean;
  sortable?: boolean;
  removable?: boolean;
  groupName?: string;
  group?: any;
  handle?: string;
}>();

const model = defineModel<any[]>({required: true, default: []});
const showRemoveConfirmationModal = ref(false);
const indexToRemove = ref<number>(-1);
const listWrapperRef = ref<HTMLElement | null>(null);

const previousPosition = ref<number | null>(null);

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const dnd = useDragAndDrop(listWrapperRef, model, {
  group: props.group,
  handle: props.handle,
});

const isDragging = computed(() => {
  return dnd.isDragging.value;
});

const tryToRemoveItem = (index: number) => {
  if (props.confirmRemove && !showRemoveConfirmationModal.value) {
    indexToRemove.value = index;
    showRemoveConfirmationModal.value = true;
  } else if (index >= 0) {
    indexToRemove.value = -1;
    dnd.remove(index);
  }
};

const keyFn = (item: any, index: number) => {
  if (typeof props.itemKey === 'string') {
    return item[props.itemKey];
  } else if (typeof props.itemKey === 'function') {
    return props.itemKey(item) || index;
  } else {
    return index;
  }
};
</script>

<style scoped>
.sortable-ghost {
  @apply bg-gray-150 text-gray-400;
}

.sortable-drag {
  @apply rounded-lg border border-gray-200 shadow-xl;
}

.handle[aria-pressed='true'] {
  @apply bg-blue-50 text-blue-500;
}

.s-placeholder:not(:first-child),
.s-placeholder:not(:last-child) {
  display: none;
}
</style>
<i18n>
{
  "en": {
    "announcements": {
      "position": "Moved to position {position}"
    }
  },
  "fr": {
    "announcements": {
      "position": "Déplacé à la position {position}"
    }
  }
}
</i18n>
